import type { CubeMemberInfo } from 'shared/src/trpc-types/cube-load-trpc-types';

const UNIT_PRICE_TYPE_NAME_KEY = 'line_items.unit_price_type_name';

const currencyFmt0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const currencyFmt2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const percentFmtIntl = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});
const percentFmt = (n: number) => `${percentFmtIntl.format(n * 100)}%`;
const numberFmt2 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const numberFmt1 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const numberFmt0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

export function formatRows(
  rows: Array<Record<string, unknown>>,
  memberMeta: Record<string, CubeMemberInfo>
): Array<Record<string, string>> {
  const hasUnitRates = Object.values(memberMeta).some(m => m.meta.is_unit_rate);

  if (hasUnitRates && !(UNIT_PRICE_TYPE_NAME_KEY in memberMeta)) {
    throw new Error(
      `Query includes unit rate fields (is_unit_rate) but is missing "${UNIT_PRICE_TYPE_NAME_KEY}" dimension. ` +
        `Add it to your query dimensions so unit rates can be adjusted for CPM.`
    );
  }

  return rows.map(row => {
    const unitPriceTypeName = row[UNIT_PRICE_TYPE_NAME_KEY] as string | undefined;
    const isCpm = unitPriceTypeName === 'CPM';
    const formatted: Record<string, string> = {};

    for (const key of Object.keys(row)) {
      let value = row[key];
      if (isCpm && memberMeta[key]?.meta.is_unit_rate) {
        const n = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(n)) value = n * 1000;
      }
      formatted[key] = formatCellValue(value, memberMeta[key]);
    }
    return formatted;
  });
}

function formatCellValue(value: unknown, meta: CubeMemberInfo | undefined): string {
  if (value === null || value === undefined) return '—';
  if (!meta) return String(value);

  if (meta.type === 'time') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? String(value) : dateFmt.format(d);
  }

  if (meta.type === 'string' || meta.type === 'boolean') return String(value);

  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);

  switch (meta.format) {
    case 'currency':
      return formatCurrency(n);
    case 'percent':
      return percentFmt(n);
    case 'number':
      return formatNumber(n);
    default:
      return formatNumber(n);
  }
}

function formatCurrency(n: number): string {
  return Math.abs(n) >= 10_000 ? currencyFmt0.format(n) : currencyFmt2.format(n);
}

function formatNumber(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 10_000) return numberFmt0.format(n);
  if (abs >= 1_000) return numberFmt1.format(n);
  return numberFmt2.format(n);
}
